import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { StatusBadge } from "../components/badges";
import { ReactionSummary } from "../components/UpdateReactions";
import { Skeleton } from "../components/ui/skeleton";

export default function MyHistory() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/me/history").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  if (!data) return <Skeleton className="h-60 w-full" />;
  return (
    <div className="space-y-8" data-testid="my-history-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("myHistory")}</h1>
      <section>
        <h2 className="font-heading font-semibold text-base mb-3">{t("myUpdates")}</h2>
        <div className="space-y-3">
          {data.updates.map((u) => (
            <div key={u.id} className="apha-card p-4" data-testid={`history-update-${u.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{u.reporting_date}</span>
                <span className="text-muted-foreground">{u.project_key} — {u.project_name}</span>
                <StatusBadge status={u.status === "SUBMITTED" ? (u.is_late ? "SUBMITTED_LATE" : "SUBMITTED_ON_TIME") : "DRAFT"} />
                {u.version > 1 && <span className="text-xs text-muted-foreground">v{u.version} · {u.versions?.length || 0} {t("versions")}</span>}
                {u.blocker_flag && <StatusBadge status="MISSING" testId={`history-blocker-${u.id}`} />}
              </div>
              <div className="text-sm mt-2 text-muted-foreground">
                <span className="text-foreground font-medium">{t("completedOutcomes")}: </span>{u.completed_summary || "—"}
              </div>
              <div className="text-sm mt-1 text-muted-foreground">
                <span className="text-foreground font-medium">{t("todayPriorities")}: </span>{u.next_summary || "—"}
              </div>
              {u.status === "SUBMITTED" && u.reactions && (u.reactions.thumbs > 0 || u.reactions.comments.length > 0) && (
                <div className="mt-2 border-t pt-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("reactionsReceived")}</div>
                  <ReactionSummary reactions={u.reactions} testId={`history-reactions-${u.id}`} />
                </div>
              )}
            </div>
          ))}
          {data.updates.length === 0 && <div className="text-sm text-muted-foreground">{t("noData")}</div>}
        </div>
      </section>
      <section>
        <h2 className="font-heading font-semibold text-base mb-3">{t("workloadHistory")}</h2>
        <div className="apha-card divide-y">
          {data.workload_checks.map((c) => (
            <div key={c.id} className="p-3 flex flex-wrap gap-4 text-sm">
              <span className="font-medium">{c.reporting_date}</span>
              <span>{t("availableCapacity")}: {c.available_capacity_percent}%</span>
              <span>{t("meetingLoad")}: {c.meeting_load}</span>
              {c.context_switch_concern && <span className="text-[#9A3412]">{t("contextSwitch")}</span>}
            </div>
          ))}
          {data.workload_checks.length === 0 && <div className="p-4 text-sm text-muted-foreground">{t("noData")}</div>}
        </div>
      </section>
    </div>
  );
}
